"use client"

import React from "react"
import Link from "next/link"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Separator } from "@/components/ui/separator"
import { Github, Linkedin, Mail, MessageCircle, Shield } from "lucide-react"
import { useTranslations, useLocale } from 'next-intl'
import { usePathname } from 'next/navigation'
import { createHashClickHandler } from '../../lib/scroll-to-hash'

export default function Footer() {
  const currentYear = new Date().getFullYear()
  const locale = useLocale()
  const rootT = useTranslations()
  const t = useTranslations('footer')
  const navT = useTranslations('nav')
  const subscribeT = useTranslations('footer.subscribe')
  const pathname = usePathname()

  const getRootLabel = (key: string, fallback: string) => {
    try {
      const value = rootT(key)
      if (!value || value === key) return fallback
      return String(value)
    } catch (e) {
      return fallback
    }
  }

  const handleCaseStudiesClick = React.useMemo(() => {
    return createHashClickHandler(locale, pathname ?? '/', 'case-studies') as unknown as (e: React.MouseEvent) => void
  }, [locale, pathname])

  const [email, setEmail] = React.useState('')
  const [statusMsg, setStatusMsg] = React.useState<string | null>(null)
  const [statusType, setStatusType] = React.useState<'idle'|'success'|'error'>('idle')
  const [isSubmitting, setIsSubmitting] = React.useState(false)

  const handleEmailClick = () => {
    window.location.href = "mailto:davidx80@hotmail.com"
  }

  const handleWhatsAppClick = () => {
    window.open("https://wa.me/573216914632", "_blank")
  }

  return (
    <footer className="bg-slate-900 dark:bg-slate-950 text-white">
      <div className="container mx-auto px-6 py-16">
        {/* Main Footer Content (Flexbox Enforcement) */}
        <div className="flex flex-col lg:flex-row justify-between items-start gap-8 lg:gap-6 xl:gap-8 mb-12 w-full">
          {/* Brand/Logo (Left) */}
          <div className="flex flex-col space-y-4 lg:w-[28%]">
            {/* ... keep content ... */}
            <h3 className="text-2xl font-bold gradient-text mb-4">{t('brandName') || 'David X. Avila'}</h3>
            <p className="text-slate-300 mb-6 max-w-md">
              {t('brandDescription') || 'Multi-disciplinary professional specializing in full-stack development, database architecture, 3D visualization, and academic research.'}
            </p>
            <div className="flex space-x-4">
              <Button
                variant="outline"
                size="icon"
                className="border-slate-600 hover:border-sky-400 hover:bg-sky-400/10 bg-transparent"
                onClick={handleEmailClick}
              >
                <Mail className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="border-slate-600 hover:border-green-400 hover:bg-green-400/10 bg-transparent"
                onClick={handleWhatsAppClick}
              >
                <MessageCircle className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="border-slate-600 hover:border-blue-400 hover:bg-blue-400/10 bg-transparent"
                onClick={() => window.open("https://www.linkedin.com/in/davidxavila", "_blank")}
              >
                <Linkedin className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="border-slate-600 hover:border-purple-400 hover:bg-purple-400/10 bg-transparent"
              >
                <Github className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Quick Links (Center-Left) */}
          <div className="flex flex-col space-y-4 flex-1">
            {/* ... keep content ... */}
            <h4 className="text-lg font-semibold mb-4">{getRootLabel('footer.quickLinks.title', t('quickLinks.title') || 'Quick Links')}</h4>
            <ul className="space-y-2">
              <li>
                <Link href={`/${locale}#services`} className="text-slate-300 hover:text-white transition-colors">
                  {t('quickLinks.services') || 'Solutions'}
                </Link>
              </li>
              <li>
                <Link href={`/${locale}#case-studies`} className="text-slate-300 hover:text-white transition-colors" onClick={handleCaseStudiesClick}>
                  {navT('portfolio') || t('quickLinks.projects') || 'Work'}
                </Link>
              </li>
              <li>
                <Link href={`/${locale}#about`} className="text-slate-300 hover:text-white transition-colors">
                  {t('quickLinks.about') || 'About'}
                </Link>
              </li>
              <li>
                <Link href={`/${locale}/blog`} className="text-slate-300 hover:text-white transition-colors">
                  {t('quickLinks.blog') || 'Blog'}
                </Link>
              </li>
              <li>
                <Link href={`/${locale}#contact`} className="text-slate-300 hover:text-white transition-colors">
                  {t('quickLinks.contact') || 'Contact'}
                </Link>
              </li>
            </ul>
          </div>

          {/* Solutions (Center-Right) */}
          <div className="flex flex-col space-y-4 flex-1">
            {/* ... keep content ... */}
            <h4 className="text-lg font-semibold mb-4">{getRootLabel('footer.services.title', t('services.title') || 'Solutions')}</h4>
            <ul className="space-y-2">
              <li>
                <Link href={`/${locale}/case-studies/architectural-visualization`} className="text-slate-300 hover:text-white transition-colors">
                  {getRootLabel('footer.services.immersivePortal', t('services.immersivePortal') || 'Immersive Pre-Sale Portal')}
                </Link>
              </li>
              <li>
                <Link href={`/${locale}/case-studies/research-management-system`} className="text-slate-300 hover:text-white transition-colors">
                  {getRootLabel('footer.services.reproducibilityAccelerator', t('services.reproducibilityAccelerator') || 'Reproducibility & Impact Accelerator')}
                </Link>
              </li>
              <li>
                <Link href={`/${locale}/case-studies/e-commerce-platform`} className="text-slate-300 hover:text-white transition-colors">
                  {getRootLabel('footer.services.productConfigurators', t('services.productConfigurators') || 'Interactive Product Configurators')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Trust & Security (Right) */}
          <div className="flex flex-col space-y-4 flex-1">
            {/* ... keep content ... */}
            <h3 className="text-sm font-semibold text-white uppercase tracking-wider">
              {t('legal.header') || 'Trust & Security'}
            </h3>
            <ul className="flex flex-col space-y-3">
              <li>
                <div className="flex items-start gap-2 p-3 rounded-lg bg-slate-900/50 border border-slate-800">
                  <Shield className="w-5 h-5 text-sky-500 mt-0.5 flex-shrink-0" />
                  <span className="text-xs text-slate-300 leading-relaxed">
                    {t('legal.dataIntegrity') || 'Data Governance & Integrity Protocols Active'}
                  </span>
                </div>
              </li>
            </ul>
          </div>

          {/* Subscribe (Far Right) */}
          <div className="flex flex-col space-y-4 lg:w-[22%]">
            {/* ... keep content ... */}
            <h4 className="text-lg font-semibold mb-2">{getRootLabel('footer.stayUpdated', t('subscribe.title') || 'Stay Updated')}</h4>
            <p className="text-slate-300">{getRootLabel('footer.subscribeDesc', t('subscribe.description') || 'Get the latest insights and project updates delivered to your inbox.')}</p>
            <div className="flex w-full md:w-auto gap-2">
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={getRootLabel('footer.emailPlaceholder', subscribeT('placeholder'))}
                className="bg-slate-800 border-slate-600 text-white placeholder:text-slate-400 focus:border-sky-400"
              />
              <Button
                onClick={async () => {
                  if (isSubmitting) return
                  setStatusType('idle')
                  setStatusMsg(null)
                  let normalized = email.trim()
                  try {
                    if ((normalized as any).normalize) normalized = (normalized as any).normalize('NFC')
                  } catch (e) {}
                  normalized = normalized.toLowerCase()
                  const re = /^[^@\s]+@[^@\s]+\.[^@\s]+$/
                  if (!re.test(normalized)) {
                    setStatusType('error')
                    setStatusMsg(subscribeT('status.invalid'))
                    return
                  }
                  setIsSubmitting(true)
                  try {
                    const res = await fetch('/api/subscribe', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ email: normalized, locale })
                    })
                    let data: any = null
                    try {
                      data = await res.json()
                    } catch {
                      try {
                        const txt = await res.text()
                        data = { __raw: txt }
                      } catch (e) {
                        data = null
                      }
                    }
                    if (res.status === 409 || data?.code === 'ALREADY_SUBSCRIBED') {
                      setStatusType('success')
                      setStatusMsg(subscribeT('status.already'))
                      setEmail('')
                    } else if (!res.ok || data?.success !== true) {
                      const err = data?.error || data?.__raw || subscribeT('status.error')
                      console.error('[subscribe] failed', { status: res.status, body: data })
                      setStatusType('error')
                      setStatusMsg(err)
                    } else {
                      setStatusType('success')
                      setStatusMsg(subscribeT('status.success'))
                      setEmail('')
                    }
                  } catch (err) {
                    console.error('[subscribe] network error', err)
                    setStatusType('error')
                    setStatusMsg(subscribeT('status.network'))
                  } finally {
                    setIsSubmitting(false)
                  }
                }}
                disabled={isSubmitting}
                className="bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700"
              >
                {isSubmitting ? `${getRootLabel('footer.subscribeBtn', subscribeT('button'))}…` : getRootLabel('footer.subscribeBtn', subscribeT('button'))}
              </Button>
            </div>
            {statusMsg && (
              <p className={`text-sm mt-2 ${statusType === 'success' ? 'text-green-500' : 'text-red-400'}`} role="status" aria-live="polite">
                {statusMsg}
              </p>
            )}
          </div>
        </div>


        <Separator className="my-8 bg-slate-800" />

        {/* Copyright and Legal Links Only (Sub-Footer) */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 w-full">
          <p className="text-slate-400 text-sm text-center md:text-left w-full md:w-auto">
            {t('copyright', { year: currentYear }) || `© ${currentYear} David X. Avila. ${getRootLabel('footer.rights', 'All rights reserved.')}`}
          </p>
          <ul className="flex flex-wrap justify-center md:justify-end gap-x-6 gap-y-2 text-sm w-full md:w-auto">
            <li>
              <Link href={`/${locale}/privacy-policy`} className="text-slate-400 hover:text-white transition-colors">
                {t('legal.privacy') || 'Privacy Policy'}
              </Link>
            </li>
            <li>
              <Link href={`/${locale}/terms-of-service`} className="text-slate-400 hover:text-white transition-colors">
                {t('legal.terms') || 'Terms of Service'}
              </Link>
            </li>
            <li>
              <Link href={`/${locale}/cookie-policy`} className="text-slate-400 hover:text-white transition-colors">
                {t('legal.cookies') || 'Cookie Policy'}
              </Link>
            </li>
          </ul>
        </div>
      </div>
    </footer>
  )
}
